import { type FC, type ReactNode } from "react";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTranslation } from "react-i18next";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";

interface ListPageHeaderProps {
  title: string;
  action?: ReactNode;
  showClearButton?: boolean;
  onClearFilter?: () => void;
  showFilterToggle?: boolean;
  filtersOpen?: boolean;
  onToggleFilters?: () => void;
  fieldsVisible?: boolean;
  children?: ReactNode;
}

// Общий "шапка страницы списка" шелл — вынесен из ProductsFilter.tsx
// (эталонная реализация референса) для повторного использования там, где
// фиче нужны свои collapsible-поля фильтра (и поэтому она не может просто
// взять готовый shared/components/Filter, ограниченный одним полем search).
export const ListPageHeader: FC<ListPageHeaderProps> = ({
  title,
  action,
  showClearButton = false,
  onClearFilter,
  showFilterToggle = false,
  filtersOpen = false,
  onToggleFilters,
  fieldsVisible = false,
  children,
}) => {
  const { t } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack spacing={isMobile ? 1 : 2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h5">{title}</Typography>
            <Box style={{ marginLeft: "auto" }}>
              <Stack spacing={1} direction="row" alignItems="center">
                {showClearButton && !isMobile && (
                  <Tooltip title={t("shared.filter.clearTooltip")}>
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      onClick={onClearFilter}
                      sx={{ marginTop: "-8px", marginBottom: "-8px" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {showFilterToggle && (
                  <Tooltip
                    title={
                      filtersOpen
                        ? t("shared.filter.closeTooltip")
                        : t("shared.filter.openTooltip")
                    }
                  >
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      onClick={onToggleFilters}
                      sx={{ marginTop: "-8px", marginBottom: "-8px" }}
                    >
                      {filtersOpen ? <CloseIcon /> : <FilterListIcon />}
                    </IconButton>
                  </Tooltip>
                )}

                {action}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {fieldsVisible && children && <Box>{children}</Box>}
      </Stack>
    </Paper>
  );
};
